import React, { useState } from "react";
import includes from "lodash/includes";
import { useMutation } from "@apollo/client";

import { Box, Card } from "@mui/material";
import RefreshIcon from "design-system/icons/layout/RefreshIcon";

import { useScreenSize } from "../../../../globals/hooks";
import { useSnackbar } from "../../../../globals/hooks";
import {
  Trip,
  Request,
  FarmedRouteStatusEnum,
  FarmRelationshipEnum,
} from "../../../../types";
import TripItineraryBlock from "./TripItineraryBlock/TripItineraryBlock";
import TripMap from "./TripMap";
import PassengerBlock from "./PassengerBlock";
import TripNotesBlock from "./TripNotesBlock";
import DispatchBlock from "./DispatchBlock";
import { FarmInDetailsBlock, FarmOutDetailsBlock } from "./farm";
import { ApolloQueryResult } from "@apollo/client";
import CommentUpdateBlock from "components/CommentUpdateBlock";
import MoovsAcceptDeclineBanner from "components/MoovsAcceptDeclineBanner";
import { ACKNOWLEDGE_TRIP_CHANGE_PROPOSAL_MUTATION } from "../../../../globals/graphql/trip.graphql";
import { tripHasChangeProposal } from "pages/requests/requests.utils";

const { Farmee, Farmor } = FarmRelationshipEnum;
const { Accepted, Pending, Completed, Declined } = FarmedRouteStatusEnum;

type TripDetailsProps = {
  trip: Trip;
  request: Request;
  requestRefetch: (variables?: {
    id: string;
  }) => Promise<ApolloQueryResult<any>>;
};

function TripDetails(props: TripDetailsProps) {
  const { trip, request, requestRefetch } = props;
  const {
    farmRelationship,
    isFarmedRoute,
    farmStatus: farmedRouteStatus,
  } = trip?.routes[0] || {};

  // hooks
  const { isMobileView } = useScreenSize();
  const snackbar = useSnackbar();

  const shouldShowDispatchBlockForFarmedRoute = includes(
    {
      [Farmee]: [Accepted, Declined, Pending, Completed],
      [Farmor]: [Declined],
    }[farmRelationship],
    farmedRouteStatus
  );

  const shouldShowFarmInDetailsBlock = includes(
    {
      [Farmee]: [Pending, Accepted, Completed],
      [Farmor]: [], // no case where we show farm in details as a farmor
    }[farmRelationship],
    farmedRouteStatus
  );

  const shouldShowFarmOutDetailsBlock = includes(
    {
      [Farmee]: [], //  no case where we show farm out details as a farmee
      [Farmor]: [Pending, Accepted, Completed, Declined],
    }[farmRelationship],
    farmedRouteStatus
  );

  const isCancelledTrip = !!trip.cancelledAt;
  const shouldShowDispatchBlock = isFarmedRoute
    ? shouldShowDispatchBlockForFarmedRoute
    : !isCancelledTrip;

  const hasTripChangeProposal = tripHasChangeProposal(trip);
  const [loadingAction, setLoadingAction] = useState<'accept' | 'decline' | null>(null);
  const [viewingTripChangeProposal, setViewingTripChangeProposal] = useState(false);
  const [snackbarSuccessMessage, setSnackbarSuccessMessage] = useState<string | null>(null);

  const [acknowledgeTripChangeProposal] = useMutation(ACKNOWLEDGE_TRIP_CHANGE_PROPOSAL_MUTATION, {
    onCompleted(data) {
      setLoadingAction(null);
        const result = data?.acknowledgeTripChangeProposal?.result;
        if (result.reason) {
          snackbar.error(result.reason);
          return;
        }

        snackbar.success(
          result.notificationDeliveryErrorMessage || snackbarSuccessMessage
        );
      
      requestRefetch();
    },
    onError(error) {
      setLoadingAction(null);
      snackbar.error(error.message);
    },
    refetchQueries: ["TripSummary"]
  });

  const handleTripChange = async (action: 'accept' | 'decline') => {
    setLoadingAction(action);
    setSnackbarSuccessMessage(`Trip changes were ${action === 'accept' ? 'applied' : 'declined'}.`);
    await acknowledgeTripChangeProposal({
      variables: {
        input: {
          tripId: trip.id,
          accept: action === 'accept'
        }
      }
    });
  };

  return (
    <Box display="flex" flexDirection="column">
      {hasTripChangeProposal && (
        <MoovsAcceptDeclineBanner
          bannerText="The customer has requested changes to this trip."
          acceptButtonText="Apply"
          declineButtonText="Decline"
          onAccept={() => handleTripChange('accept')}
          onDecline={() => handleTripChange('decline')}
          icon={<RefreshIcon />}
          acceptLoading={loadingAction === 'accept'}
          declineLoading={loadingAction === 'decline'}
        />
      )}
      <Box display="flex" flexDirection={isMobileView ? "column" : "row"}>
        {/* trip itinerary/timeline */}
        <Box
          mr={isMobileView ? 0 : 2}
          mb={isMobileView ? 2 : 0}
          display="flex"
          flexDirection="column"
          flex="1"
        >
          <TripItineraryBlock trip={trip} setViewingTripChangeProposal={setViewingTripChangeProposal} />
          {/* trip notes */}
          <Box mt={2}>
            <TripNotesBlock trip={trip} />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" flex="1">
          {/* map */}
          {!!trip.googleDirectionsResult && (
            <Box maxHeight={86} mb={2}>
              <TripMap request={request} trip={trip} viewingTripChangeProposal={viewingTripChangeProposal} />
            </Box>
          )}

          {/* Farming Blocks */}
          {isFarmedRoute && (
            <Box mb={2}>
              {/* farm in */}
              {shouldShowFarmInDetailsBlock && (
                <FarmInDetailsBlock route={trip.routes[0]} />
              )}

              {/* farm out */}
              {shouldShowFarmOutDetailsBlock && (
                <FarmOutDetailsBlock request={request} trip={trip} />
              )}
            </Box>
          )}

          {/* dispatch block */}
          {shouldShowDispatchBlock && (
            <Box mb={2}>
              <DispatchBlock request={request} trip={trip} />
            </Box>
          )}

          {/* passenger */}
          <Box mb={2}>
            <PassengerBlock trip={trip} />
          </Box>
        </Box>
      </Box>

      <Box>
        <Card variant="outlined">
          <Box p={2}>
            <CommentUpdateBlock
              mode={
                isFarmedRoute && farmRelationship === Farmee
                  ? "farmee"
                  : "tripDetails"
              }
              comments={trip.comments}
              tripId={trip.id}
              refetchQuery={requestRefetch}
            />
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

export default TripDetails;
